import {Component} from '@angular/core';
import {TablePageComponent} from '../../pages-base/table-page.component';
import {
  ColumnPropertyGroup,
  FiltersValues,
  RecordData,
  RecordsDataRequestQueryParams,
  RecordTableColumnParams,
  RecordTableParams
} from '../../../../core/types/records-table.types';
import {Pageable} from '../../../../core/types/pagable.types';
import {CommonModule} from '@angular/common';
import {RecordsTableModule} from '../../../../core/components/records-table/records-table.module';
import {defaultRecordTableParams} from "../../../../core/models/default-record-table-params";
import {defaultPageableRecordData} from "../../../../core/models/default-pageable-record-data";
import {smoothShowHideHeight} from '../../../../shared/animations/smoothShowHideAnimations';
import {ResourceParams, resources} from "../../../../core/models/resource/resources";
import {
  RecordsTableFiltersComponent
} from "../../../../core/components/records-table-filters/records-table-filters.component";
import {TableContextService} from "../../../../core/services/table-services/table-context.service";
import { PageInfoComponent } from "../../../../core/components/page-info/page-info.component";

@Component({
  selector: 'app-pulse-report',
  standalone: true,
  imports: [
    CommonModule,
    RecordsTableModule,
    RecordsTableFiltersComponent,
    PageInfoComponent
  ],
  templateUrl: './pulse-report-page.component.html',
  styleUrl: './pulse-report-page.component.scss',
  animations: [smoothShowHideHeight],
  providers: [ TableContextService ]
})
export class PulseReportPageComponent extends TablePageComponent {
  public readonly resourceParams: ResourceParams = resources['pulse-reports'];
  protected tableColumnParams: RecordTableParams | null = null;
  protected tableData: Pageable<RecordData> | null = null;
  protected groups: Array<ColumnPropertyGroup> = [];
  protected reviewersDisplayingState: Record<number, boolean> = {};

  public override ngOnInit(): void {
    super.ngOnInit();
    this.initiateTableData().then();
  }

  protected handleFiltersChange(recordsTableFiltersValues: FiltersValues): void {
    const queryParams = this.getQueryParamsAccordingToFilters(recordsTableFiltersValues);
    this.updateTableData(queryParams).then();
  }

  protected getRowColor(rowColor?: string): string {
    if (!rowColor) { return  'unset'; }

    return this.utilityService.hexToRgba(rowColor, 0.1);
  }

  protected getWidthStylesForColumn(column: RecordTableColumnParams): Record<string, string> {
    return {
      'width': `${column.columnProperties.WIDTH?.value}px`,
      'flex-basis': `${column.columnProperties.WIDTH?.value}px`
    }
  }

  protected isReviewer(row: RecordData): boolean {
    return !!(row['isReviewer'] && row.id !== null)
  }

  protected handleRowClick(row: RecordData): void {
    if (!this.isReviewer(row)) { return; }

    const managersForCurrentReviewer: Array<RecordData> = this.getManagersForCurrentReviewer(row);
    if (!managersForCurrentReviewer.length) { return; }

    this.toggleManagersList(managersForCurrentReviewer);
    this.reviewersDisplayingState = {
      ...this.reviewersDisplayingState,
      [row.id]: !this.reviewersDisplayingState[+row.id]
    }
  }

  protected getManagersForCurrentReviewer(reviewerRecordData: RecordData): Array<RecordData> {
    const tableContent: Array<RecordData> = this.tableData!.content;
    const managersForCurrentReviewer = [];
    const reviewerIndex: number = this.tableData!.content.findIndex((recordData: RecordData) => recordData.id === reviewerRecordData.id);

    for (let i = reviewerIndex + 1; i < tableContent.length; i++) {
      if (!tableContent[i]) { break; }
      if (this.isReviewer(tableContent[i])) { break; }

      managersForCurrentReviewer.push(tableContent[i]);
    }

    return managersForCurrentReviewer;
  }

  private async initiateTableData(): Promise<void> {
    this.tableColumnParams = await this.getRecordTableColumnsParams(this.resourceParams.path).catch(() => defaultRecordTableParams);
    this.groups = this.getGroupsFromRecordTableColumnsParams(this.tableColumnParams.fields);
  }

  private async updateTableData(queryParams: RecordsDataRequestQueryParams): Promise<void> {
    this.tableData = await this.getRecordTableData(this.resourceParams.path, queryParams).catch(() => defaultPageableRecordData);
    this.tableData.content.forEach((row: RecordData) => row['isOpen'] = true);
    this.tableData.content
      .filter((row: RecordData) => this.isReviewer(row))
      .forEach((row: RecordData) => this.reviewersDisplayingState[+row.id] = true);
  }

  private toggleManagersList(managersList: Array<RecordData>): void {
    const isManagersListOpened: boolean = !!managersList[0]['isOpen'];
    isManagersListOpened ? this.closeManagersList(managersList) : this.openManagersList(managersList);
  }

  private openManagersList(managersList: Array<RecordData>): void {
    managersList.forEach((manager: RecordData) => {
      manager['isOpen'] = true;
    })
  }

  private closeManagersList(managersList: Array<RecordData>): void {
    managersList.forEach((manager: RecordData) => {
      manager['isOpen'] = false;
    })
  }
}
